import { useState } from "react";
import { Video } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import type { UseMediaDevices } from "../hooks/useMediaDevices";

interface Props {
  open: boolean;
  onClose: () => void;
  devices: UseMediaDevices;
  /** Live-switch callbacks (in-room); omit on the pre-join screen. */
  onSwitchMic?: (deviceId: string) => void;
  onSwitchCamera?: (deviceId: string) => void;
  /**
   * "Udostępnij kamerę i mikrofon" — (re)request permission. Returns true on
   * success. Provide it wherever access might not have been granted yet (works
   * even after a previous denial: it re-prompts). Omit to hide the button.
   */
  onRequestAccess?: () => Promise<boolean>;
  /** Whether any local media is already granted (hides the share button). */
  hasMedia?: boolean;
}

const SELECT =
  "w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-sm text-[var(--color-text-primary)]";

export function DeviceSettingsDialog({
  open,
  onClose,
  devices,
  onSwitchMic,
  onSwitchCamera,
  onRequestAccess,
  hasMedia = false,
}: Props) {
  const [requesting, setRequesting] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleShare = async () => {
    if (!onRequestAccess) return;
    setRequesting(true);
    setFailed(false);
    try {
      const ok = await onRequestAccess();
      setFailed(!ok);
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Urządzenia" size="sm">
      <div className="space-y-4">
        {onRequestAccess && !hasMedia && (
          <div className="space-y-1.5 rounded-md border border-[var(--color-border)] bg-black/[.02] p-3 dark:bg-white/[.03]">
            <button
              type="button"
              disabled={requesting}
              onClick={() => void handleShare()}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-[var(--color-primary)] px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              <Video size={15} />
              {requesting ? "Proszę o zgodę…" : "Udostępnij kamerę i mikrofon"}
            </button>
            {failed ? (
              <p className="text-xs text-red-500">
                Brak dostępu. Jeśli wcześniej odmówiono zgody, kliknij ikonę
                kłódki obok adresu strony i zezwól na kamerę/mikrofon, a potem
                spróbuj ponownie.
              </p>
            ) : (
              <p className="text-xs text-[var(--color-text-muted)]">
                Możesz udzielić zgody teraz — także jeśli wcześniej się nie
                udało.
              </p>
            )}
          </div>
        )}
        <label className="block text-sm">
          <span className="mb-1 block text-[var(--color-text-secondary)]">Kamera</span>
          <select
            className={SELECT}
            value={devices.prefs.cameraId ?? ""}
            onChange={(e) => {
              const id = e.target.value || null;
              devices.setPref("cameraId", id);
              if (id && onSwitchCamera) onSwitchCamera(id);
            }}
          >
            <option value="">Domyślna</option>
            {devices.cameras.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-[var(--color-text-secondary)]">Mikrofon</span>
          <select
            className={SELECT}
            value={devices.prefs.micId ?? ""}
            onChange={(e) => {
              const id = e.target.value || null;
              devices.setPref("micId", id);
              if (id && onSwitchMic) onSwitchMic(id);
            }}
          >
            <option value="">Domyślny</option>
            {devices.microphones.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
        </label>

        {devices.canPickSpeaker && (
          <label className="block text-sm">
            <span className="mb-1 block text-[var(--color-text-secondary)]">Głośnik</span>
            <select
              className={SELECT}
              value={devices.prefs.speakerId ?? ""}
              onChange={(e) => devices.setPref("speakerId", e.target.value || null)}
            >
              <option value="">Domyślny</option>
              {devices.speakers.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <p className="text-xs text-[var(--color-text-muted)]">
          Nazwy urządzeń pojawiają się po udzieleniu zgody na kamerę/mikrofon
          {onRequestAccess && !hasMedia
            ? " — użyj przycisku powyżej, jeśli są puste."
            : "."}
        </p>
      </div>
    </Modal>
  );
}
